import { ChevronRight, X } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { I18nKey } from "@/lib/i18n";
import { useT } from "@/lib/i18n";
import { type ScenarioStatus, type ScenarioSummary, useScenarios } from "@/lib/scenarios";
import { CapstoneStar, ScenStatusDot } from "./atoms";

type LockedExplainSheetProps = {
  scenario: ScenarioSummary | null;
  onClose: () => void;
  // Optional: when a prereq inside the sheet is *itself* LOCKED,
  // tapping it doesn't navigate away — instead the sheet swaps to
  // explaining that prereq's own block. The parent owns the state
  // (see `Skills.tsx` / `SkillsTree.tsx`) and just re-points its
  // `lockedFocus`. Callers without this prop fall back to the
  // navigation path on every prereq tap.
  onFocus?: (scenario: ScenarioSummary) => void;
};

// Maps ScenarioStatus → i18n key for the prereq status microcopy.
const STATUS_LABEL_KEYS: Record<ScenarioStatus, I18nKey> = {
  COMPLETED: "skills.locked_status_done",
  NEEDS_REVIEW: "skills.locked_status_done",
  READY_FOR_CHECKPOINT: "skills.locked_status_ready_check",
  IN_PROGRESS: "skills.locked_status_in_progress",
  AVAILABLE: "skills.locked_status_available",
  LOCKED: "skills.locked_status_locked",
};

/**
 * Bottom sheet shown when the user taps a LOCKED row. Lists the
 * unmet prerequisites with their current statuses (so progress is
 * legible: "1 of 2 done"), plus a human "Почему?" explanation.
 *
 * Mockup: `design/hifi/scenarios.jsx` `LockedExplainScreen` lines
 * ~1218–1307.
 *
 * Renders nothing when `scenario` is null — caller controls
 * mount/unmount via that prop.
 */
export function LockedExplainSheet({ scenario, onClose, onFocus }: LockedExplainSheetProps) {
  const t = useT();
  // Pull the full scenario list so we can resolve prereq slugs to
  // titles + their current per-user status. The list is already
  // cached from the journal page; this hook just reuses it.
  const scenarios = useScenarios();

  // Close on Esc — sheet semantics expect this on desktop.
  useEffect(() => {
    if (!scenario) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scenario, onClose]);

  if (!scenario) return null;

  const allByslug = new Map((scenarios.data ?? []).map((s) => [s.slug, s]));
  const prereqs = scenario.prerequisiteSlugs.map((slug) => allByslug.get(slug)).filter(Boolean) as ScenarioSummary[];

  return (
    <>
      {/* Backdrop — click anywhere outside the sheet to dismiss. */}
      <button type="button" onClick={onClose} aria-label="close" className="fixed inset-0 z-40 bg-black/45" />
      {/* Sheet itself — anchored to bottom on mobile, centered on
          larger screens via max-width + mx-auto. */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-[640px] rounded-t-[20px] border-t border-border bg-surface px-5 pb-5 pt-3.5 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]"
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
      >
        {/* Drag handle — purely visual. */}
        <div className="mx-auto mb-3.5 h-1 w-9 rounded-full" style={{ background: "var(--surface-3)" }} />

        <div className="mb-3.5 flex items-start gap-3">
          <ScenStatusDot status="LOCKED" />
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[16px] font-bold text-text">{scenario.title}</span>
              {scenario.capstone && (
                <span
                  className="inline-flex items-center gap-[3px] rounded px-[5px] py-[1px] font-mono text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: "var(--scen-bg)", color: "var(--scen-fg)" }}
                >
                  <CapstoneStar size={9} />
                  {t("skills.tag_capstone")}
                </span>
              )}
            </div>
            <p className="text-[12px] leading-snug text-text-3">{scenario.promise}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="-mr-1 rounded-md p-1.5 text-text-3 hover:bg-[color:var(--surface-2)]"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">
          {t("skills.locked_why_title")}
        </div>
        <div className="mb-3.5 flex flex-col gap-1.5">
          {prereqs.map((p) => {
            const status = p.progress?.status ?? "AVAILABLE";
            // A prereq that is itself hard-LOCKED has no actionable
            // Detail — chain into the sheet instead, recursing the
            // same UI on the prereq's own blockers. Callers that
            // don't pass `onFocus` (legacy) get the navigation path.
            const isLockedPrereq = status === "LOCKED";
            const cellClass = cn(
              "flex w-full items-center gap-2.5 rounded-[var(--r-sm)] border bg-[color:var(--surface-2)] px-3 py-2.5 text-left",
            );
            const cellStyle = { borderColor: "var(--border)" };
            const inner = (
              <>
                <ScenStatusDot status={status} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-text">{p.title}</div>
                  <div className="font-mono text-[11px] text-text-3">{t(STATUS_LABEL_KEYS[status])}</div>
                </div>
                <ChevronRight className="size-3.5 shrink-0 text-text-3" />
              </>
            );
            if (isLockedPrereq && onFocus) {
              return (
                <button key={p.slug} type="button" onClick={() => onFocus(p)} className={cellClass} style={cellStyle}>
                  {inner}
                </button>
              );
            }
            return (
              <Link key={p.slug} to={`/skills/${p.slug}`} onClick={onClose} className={cellClass} style={cellStyle}>
                {inner}
              </Link>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-[var(--r-md)] border border-border bg-[color:var(--surface-2)] py-3 text-[14px] font-medium text-text-2 transition-colors hover:bg-[color:var(--surface-3)]"
        >
          {t("skills.locked_back")}
        </button>
      </div>
    </>
  );
}
